import React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { OutlineButton } from '../../components/Buttons';
import BoqContainer from '../BOQ/BoqContainer';
import { designsPortfolioReturnHref } from './designsRoute';

const AddPortfolioBoq = () => {
  const navigate = useNavigate();
  const { portfolioId } = useParams();
  const location = useLocation();
  const portfolioWizardReturnPath =
    location.state?.portfolioEditReturnPath || '/interior-designer/add-portfolio';

  const steps = [
    { id: 'portfolio', name: 'Portfolio Details', number: 1 },
    { id: 'metadata', name: 'Design Metadata', number: 2 },
    { id: 'boq', name: 'Bill of Quantities', number: 3 },
  ];

  const currentStep = 'boq';

  return (
    <div className="flex h-[calc(100vh-140px)] overflow-hidden">
      {/* Left Sidebar - Stepper */}
      <div className="w-80 flex-shrink-0 bg-white border-r border-gray-200">
        <div className="p-6 h-full flex flex-col">
          <div className="flex items-center gap-4 mb-4">
            <OutlineButton
              onClick={() => navigate(designsPortfolioReturnHref())}
              className="w-fit p-2 hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </OutlineButton>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Add Portfolio Item</h1>
              <p className="text-xs text-gray-500 mt-0.5">Bill of Quantities</p>
            </div>
          </div>

          <div className="space-y-2 flex-1">
            {steps.map((step) => {
              const isActive = currentStep === step.id;
              const isCompleted = ['portfolio', 'metadata'].includes(step.id);

              return (
                <div key={step.id} className="relative">
                  <button
                    onClick={() => {
                      if (step.id === 'portfolio' || step.id === 'metadata') {
                        navigate(portfolioWizardReturnPath);
                      }
                    }}
                    className={`w-full text-left p-4 transition-all duration-200 ${
                      isActive
                        ? 'bg-flamingo-50 text-flamingo-700 border-l-4 border-flamingo-500'
                        : isCompleted
                        ? 'text-gray-700 hover:text-gray-800 hover:bg-gray-50'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-8 h-8 rounded-md flex items-center justify-center text-sm font-medium ${
                          isActive
                            ? 'bg-flamingo-500 text-white'
                            : isCompleted
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {isCompleted ? (
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : (
                          step.number
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">{step.name}</div>
                      </div>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right Side - BOQ Table Content */}
      <div className="flex-1 bg-gray-50 overflow-hidden flex flex-col p-6">
        <div className="flex flex-col h-full min-h-0">
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Bill of Quantities (BOQ)</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Add materials with quantity, unit price, and total. Price and total columns are shown.
            </p>
          </div>
          <div className="flex-1 min-h-0 flex flex-col">
            <BoqContainer
              portfolioId={portfolioId}
              hidePriceColumns={false}
              onSuccess={() => navigate(designsPortfolioReturnHref())}
              onCancel={() => navigate(designsPortfolioReturnHref())}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPortfolioBoq;
